import { useState, useEffect, useCallback, useRef } from 'react';
import apiClient from '../../../lib/apiClient';

/**
 * Hook for fetching and managing capability suggestions based on procurement data.
 * Provides suggestions for auto-populating vendor/product fields and fetching
 * transaction details.
 *
 * @param {number} departmentId - The department ID to fetch suggestions for
 * @returns {Object} Suggestions state and helper functions
 */
export function useSuggestions(departmentId) {
  const [suggestions, setSuggestions] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Cache for transaction data
  const transactionsCache = useRef({});

  // Fetch suggestions when departmentId changes
  const fetchSuggestions = useCallback(async () => {
    if (!departmentId) {
      setSuggestions({});
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await apiClient.get(
        `/api/zero-trust/departments/${departmentId}/capability-suggestions`
      );
      setSuggestions(data || {});
    } catch (err) {
      console.error('Error fetching capability suggestions:', err);
      setError(err.message || 'Failed to fetch suggestions');
      setSuggestions({});
    } finally {
      setLoading(false);
    }
  }, [departmentId]);

  useEffect(() => {
    fetchSuggestions();
    // Clear transactions cache when department changes
    transactionsCache.current = {};
  }, [fetchSuggestions]);

  /**
   * Get suggestion for a specific capability code
   * @param {string} capabilityCode - The capability code (e.g., 'idp', 'mfa', 'edr')
   * @returns {Object|null} Suggestion data or null if not found
   */
  const getSuggestionForCapability = useCallback((capabilityCode) => {
    if (!capabilityCode || !suggestions) return null;
    const code = capabilityCode.toLowerCase();
    const suggestion = suggestions[code];

    if (!suggestion || !suggestion.topVendor) return null;

    return {
      vendor: suggestion.topVendor,
      product: suggestion.topProduct,
      confidence: suggestion.confidence || 0,
      totalSpend: suggestion.totalSpend || 0,
      productCount: suggestion.productCount || 0,
      detected: suggestion.detected || []
    };
  }, [suggestions]);

  /**
   * Fetch transactions for a specific capability
   * @param {string} capabilityCode - The capability code
   * @returns {Promise<Object>} Transaction data
   */
  const fetchTransactions = useCallback(async (capabilityCode) => {
    if (!departmentId || !capabilityCode) {
      return { transactions: [], count: 0, totalSpend: 0 };
    }

    const cacheKey = `${departmentId}_${capabilityCode}`;

    // Check cache first
    if (transactionsCache.current[cacheKey]) {
      return transactionsCache.current[cacheKey];
    }

    try {
      const data = await apiClient.get(
        `/api/zero-trust/departments/${departmentId}/capability/${capabilityCode}/transactions`
      );

      // Cache the result
      transactionsCache.current[cacheKey] = data;

      return data;
    } catch (err) {
      console.error('Error fetching transactions:', err);
      return {
        transactions: [],
        count: 0,
        totalSpend: 0,
        error: err.message || 'Failed to fetch transactions'
      };
    }
  }, [departmentId]);

  /**
   * Check if there's a suggestion available for a capability
   * @param {string} capabilityCode - The capability code
   * @returns {boolean} True if suggestion exists
   */
  const hasSuggestion = useCallback((capabilityCode) => {
    const suggestion = getSuggestionForCapability(capabilityCode);
    return suggestion !== null && suggestion.confidence > 0;
  }, [getSuggestionForCapability]);

  /**
   * Refresh suggestions from the server
   */
  const refresh = useCallback(() => {
    transactionsCache.current = {};
    return fetchSuggestions();
  }, [fetchSuggestions]);

  return {
    suggestions,
    loading,
    error,
    getSuggestionForCapability,
    fetchTransactions,
    hasSuggestion,
    refresh
  };
}

export default useSuggestions;
